import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import { useBooth } from '../../../../../../../hooks/useBooth';
import { usePermission } from '../../../../../../../hooks/usePermission';
import { Booth, EventInterface } from '../../../../../../../interfaces/interfaces';
import { fetchAndSetBooths } from '../../../../../../../reduxToolkit/slices/commonSlice';
import { useSelector, useDispatch } from '../../../../../../../reduxToolkit/typedRedux';
import ConfigTable, { configSection } from '../../../../../../account/ConfigTable';
import { BoothLimitSelect } from '../../../../../../account/event/BoothLimitSelect';
import { BoothForwardingSelect } from '../../../../../../account/event/BoothQueueForwardingSelect';
import VyDropdownSelect from '../../../../../../common/VyDropdownSelect';

const BoothConfigTab = ({ booth }: { booth: Booth }) => {
  const [configSections, setConfigSections] = useState<configSection[]>([]);
  const event: EventInterface | null = useSelector((state) => state.common.event);
  const user = useSelector((state) => state.common.user);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isEventHost } = usePermission();
  const { updateProp } = useBooth();

  async function handleSettingChange(value: any, path: any) {
    await updateProp(booth._id, path, value);
    await fetchBooths();
  }

  async function fetchBooths() {
    if (!event) return;
    try {
      dispatch(fetchAndSetBooths(event.slug));
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    updateSections(booth);
  }, [booth]);

  const isExternalBooth = booth.organization._id !== event?.organization?._id;

  function updateSections(booth: Booth) {
    setConfigSections([
      {
        title: t('config:booth-sections.limits'),
        items: [
          ...(!booth.hasUnlimitedRooms
            ? [
                {
                  title: t('account:meetingrooms'),
                  description: t('config:maxBoothMeetingrooms-description'),
                  value: undefined,
                  path: '',
                  type: 'custom',
                  hide: !event?.videomodes.meeting.active || !isEventHost(user),
                  renderComponent: () => (
                    <BoothLimitSelect
                      boothId={booth._id}
                      value={booth.maxMeetings}
                      currentExistingNumber={booth.meetingrooms?.length}
                      unlimited={booth.hasUnlimitedRooms}
                      name="maxMeetings"
                      id="max-meetings"
                      onUpdate={() => fetchBooths()}
                    />
                  ),
                },
                {
                  title: t('account:presentations'),
                  description: t('config:maxBoothPresentations-description'),
                  value: undefined,
                  path: '',
                  type: 'custom',
                  hide: !event?.videomodes.presentation.active || !isEventHost(user),
                  renderComponent: () => (
                    <BoothLimitSelect
                      boothId={booth._id}
                      value={booth.maxPresentations}
                      currentExistingNumber={booth.presentationrooms?.length}
                      unlimited={booth.hasUnlimitedRooms}
                      name="maxPresentations"
                      id="max-presentations"
                      onUpdate={() => fetchBooths()}
                    />
                  ),
                },
                {
                  title: t('account:conferencerooms'),
                  description: t('config:maxBoothConferencerooms-description'),
                  value: undefined,
                  path: '',
                  type: 'custom',
                  hide: !event?.videomodes.conference.active || !isEventHost(user),
                  renderComponent: () => (
                    <BoothLimitSelect
                      boothId={booth._id}
                      value={booth.maxConferences}
                      currentExistingNumber={booth.conferencerooms?.length}
                      unlimited={booth.hasUnlimitedRooms}
                      name="maxConferences"
                      id="max-conferences"
                      onUpdate={() => fetchBooths()}
                    />
                  ),
                },
                {
                  title: t('account:max-pdf-uploads'),
                  description: t('config:maxBoothPdfUpload-description'),
                  value: undefined,
                  path: '',
                  type: 'custom',
                  hide: !event?.settings?.maxBoothPdfUpload || event?.settings?.maxBoothPdfUpload === 0 || !isEventHost(user),
                  renderComponent: () => (
                    <BoothLimitSelect
                      maxValue={event?.settings?.maxBoothPdfUpload}
                      boothId={booth._id}
                      value={booth.maxPdfUploads !== null ? booth.maxPdfUploads : event?.settings?.maxBoothPdfUpload}
                      currentExistingNumber={booth.pdfUploads?.length || 0}
                      unlimited={booth.hasUnlimitedRooms}
                      name="maxPdfUploads"
                      id="max-conferences"
                      onUpdate={() => fetchBooths()}
                    />
                  ),
                },
              ]
            : ([] as any)),
        ],
      },
      {
        title: t('config:booth-sections.UI'),
        items: [
          {
            title: t('config:displayPresentationsInBooths-title'),
            description: t('config:displayPresentationsInBooths-description'),
            hide: !booth?.settings?.displayPresentationsInBooths.allowSelfSelectValues && !isEventHost(user),
            value: booth?.settings?.displayPresentationsInBooths.value,
            path: 'settings.displayPresentationsInBooths.value',
            type: 'boolean',
          },
          {
            title: t('config:displayContactShareInBooth-title'),
            description: t('config:displayContactShareInBooth-description'),
            hide: (!booth?.settings?.displayContactShareInBooth?.allowSelfSelectValues && !isEventHost(user)) || event?.modules.contactshare.active === false,
            value: booth?.settings?.displayContactShareInBooth?.value,
            path: 'settings.displayContactShareInBooth.value',
            type: 'boolean',
          },
          {
            title: t('config:displayNetworkingroomsInBooth-title'),
            description: t('config:displayNetworkingroomsInBooth-description'),
            hide: !event?.modules?.networking?.active || !isEventHost(user),
            value: booth?.settings?.displayNetworkingroomsInBooth?.value,
            path: 'settings.displayNetworkingroomsInBooth.value',
            type: 'boolean',
          },
        ],
      },
      {
        title: t('config:booth-sections.exhibitor'),
        items: [
          {
            title: t('config:canEditMeetingSettings-title'),
            description: t('config:canEditMeetingSettings-description'),
            path: 'settings.canEditMeetingSettings.value',
            type: 'boolean',
            hide: !isEventHost(user) && !isExternalBooth,
            value: booth.settings.canEditMeetingSettings.value,
          },
          {
            title: t('config:externalSpeakers-title'),
            description: t('config:externalSpeakers-description'),
            path: 'settings.externalSpeakers.isEnabled',
            type: 'boolean',
            hide: !isEventHost(user),
            value: booth.settings.externalSpeakers.isEnabled,
          },
          {
            title: t('config:allowExternalSpeakerEdit-title'),
            description: t('config:allowExternalSpeakerEdit-description'),
            path: 'settings.externalSpeakers.canExternalSpeakerEdit',
            type: 'boolean',
            hide: !isEventHost(user),
            value: booth.settings.externalSpeakers.canExternalSpeakerEdit,
          },
          {
            title: t('config:showBoothStats-title'),
            description: t('config:showBoothStats-description'),
            path: 'settings.showBoothStats.value',
            type: 'boolean',
            hide: !isEventHost(user),
            value: booth.settings.showBoothStats.value,
          },
          {
            title: t('config:showAutoReply-title'),
            description: t('config:showAutoReply-description'),
            path: 'settings.showAutoReply.value',
            type: 'boolean',
            hide: !isEventHost(user),
            value: booth.settings.showAutoReply.value,
          },
          {
            title: t('config:allowedQueueForwarding-title'),
            description: t('config:allowedQueueForwarding-description'),
            hide: event?.settings.queuemode !== 'manual' || !isEventHost(user),
            type: 'custom',
            path: '',
            value: undefined,
            renderComponent: () => (
              <VyDropdownSelect
                name="allowedQueueForwarding"
                value={booth?.allowedQueueForwarding}
                options={[
                  { value: 'booth', title: 'Standintern' },
                  { value: 'global', title: 'Global' },
                  { value: 'selected', title: 'Custom' },
                  { value: 'none', title: 'Deaktiviert' },
                ]}
                onChange={(value) => handleSettingChange(value, 'allowedQueueForwarding')}
              />
            ),
          },
          {
            title: t('config:allowedQueueForwardingSelect-title'),
            description: t('config:allowedQueueForwardingSelect-description'),
            hide: event?.settings.queuemode !== 'manual' || !isEventHost(user),
            path: '',
            value: undefined,
            type: 'custom',
            renderComponent: () => (
              <BoothForwardingSelect
                onSubmit={(value) => handleSettingChange(value, 'selectedQueueForwardingBooths')}
                items={event?.booths?.map(({ name, _id }) => ({ name, _id })) || []}
                selectedValues={booth?.selectedQueueForwardingBooths || []}
                disabled={booth.allowedQueueForwarding !== 'selected'}
              />
            ),
          },
        ],
      },
      {
        title: t('config:booth-sections.appointment-settings'),
        items: [
          {
            title: t('config:enableAppointments-title'),
            description: t('config:enableAppointments-description'),
            hide: !event?.modules?.booking?.active || !booth?.settings?.enableAppointments?.allowSelfSelectValues,
            value: booth?.settings?.enableAppointments.value,
            path: 'settings.enableAppointments.value',
            type: 'boolean',
          },
          {
            title: t('config:enableAppointmentExhibitorEmail-title'),
            description: t('config:enableAppointmentExhibitorEmail-description'),
            hide: !event?.modules?.booking?.active || !booth?.settings?.enableAppointments?.allowSelfSelectValues,
            value: booth?.settings?.enableAppointmentExhibitorEmail?.value,
            path: 'settings.enableAppointmentExhibitorEmail.value',
            type: 'boolean',
          },
        ],
      },
      {
        title: t('config:booth-sections.self-selection'),
        items: [
          {
            title: t('config:displayPresentationsInBooths-selfSelect-title'),
            description: t('config:displayPresentationsInBooths-selfSelect-description'),
            path: 'settings.displayPresentationsInBooths.allowSelfSelectValues',
            type: 'boolean',
            hide: !isEventHost(user) || !isExternalBooth,
            value: booth.settings.displayPresentationsInBooths.allowSelfSelectValues,
          },
          {
            title: t('config:displayContactShareInBooth-selfSelect-title'),
            description: t('config:displayContactShareInBooth-selfSelect-description'),
            path: 'settings.displayContactShareInBooth.allowSelfSelectValues',
            type: 'boolean',
            hide: !isEventHost(user) || !isExternalBooth,
            value: booth.settings.displayContactShareInBooth.allowSelfSelectValues,
          },
          {
            title: t('config:enableAppointments-selfSelect-title'),
            description: t('config:enableAppointments-selfSelect-description'),
            path: 'settings.enableAppointments.allowSelfSelectValues',
            type: 'boolean',
            hide: !isEventHost(user) || !isExternalBooth,
            value: booth.settings.enableAppointments.allowSelfSelectValues,
          },
        ],
      },
    ]);
  }

  return (
    <ConfigTable
      onChange={(value: any, path: string) => {
        return handleSettingChange(value, path);
      }}
      configSections={configSections}
    />
  );
};
export default BoothConfigTab;
